import storage from "store"

/** v4：按内容配默认列宽、占满可视区；旧记忆列宽不再覆盖新默认 */
const PREFIX = "52Tyre-Admin-ColumnSetting-v4-"

/**
 * 生成稳定列 ID
 */
export function getColumnId(col, index) {
  if (col._colId) return col._colId
  if (col.key != null && col.key !== "") return String(col.key)
  if (col.dataIndex != null && col.dataIndex !== "") {
    return String(col.dataIndex)
  }
  if (col.scopedSlots && col.scopedSlots.customRender) {
    return String(col.scopedSlots.customRender)
  }
  if (col.title) return `title:${col.title}`
  return `col:${index}`
}

/**
 * 是否为行内「操作」列
 */
export function isOperationColumn(col) {
  if (!col) return false
  if (col.title === "操作") return true
  if (col.key === "operation") return true
  if (
    col.scopedSlots &&
    col.scopedSlots.customRender === "operation"
  ) {
    return true
  }
  return false
}

/**
 * 操作列 / fixed 列锁定：不可隐藏，排序时保持在末尾固定区
 */
export function isLockedColumn(col) {
  if (!col) return false
  if (col.fixed) return true
  return isOperationColumn(col)
}

/** 多按钮操作列最小宽度，避免「详情/指派」等被裁切 */
const OP_COL_MIN_WIDTH = 156

/**
 * 保留列表配置的默认列宽（不再收紧），仅兜底过小值
 * 过长文本仍靠 ellipsis；横向占满由各模块合理的初始 width 保证
 */
function compactContentWidth(width) {
  const w = Number(width)
  if (!Number.isFinite(w) || w <= 0) return width
  return Math.max(52, Math.round(w))
}

/**
 * 统一操作列：注入 className（字号/不换行/收紧间距由全局样式控制）
 * 保证多操作场景列宽足够；单按钮窄列（如 width < 100）保持原宽
 */
function decorateOperationColumn(col) {
  const existing = (col.className || "")
    .split(/\s+/)
    .filter(Boolean)
    .filter(c => c !== "erp-table-operation-col")
  col.className = ["erp-table-operation-col", ...existing].join(" ")

  if (!col._opWidthDecorated) {
    if (typeof col.width !== "number") {
      col.width = OP_COL_MIN_WIDTH
    } else if (col.width >= 100) {
      col.width = Math.max(col.width, OP_COL_MIN_WIDTH)
    }
  }
  col._opWidthDecorated = true
  return col
}

/**
 * 内容列：规范化默认宽度 + 开启省略
 */
function decorateContentColumn(col) {
  if (!col._contentWidthDecorated && typeof col.width === "number") {
    col.width = compactContentWidth(col.width)
  }
  col._contentWidthDecorated = true
  if (col.ellipsis == null) {
    col.ellipsis = true
  }
  return col
}

/**
 * 深拷贝列定义（保留 sorter 等函数引用），并保证 _colId 唯一
 * 操作列保证可读宽度；其它列保留配置宽度并开启 ellipsis
 */
export function cloneColumns(columns = []) {
  const used = new Set()
  return (columns || []).map((col, index) => {
    const next = { ...col }
    if (col.scopedSlots) {
      next.scopedSlots = { ...col.scopedSlots }
    }
    let id = getColumnId(col, index)
    if (used.has(id)) {
      id = `${id}__${index}`
    }
    used.add(id)
    next._colId = id
    if (isOperationColumn(next)) {
      decorateOperationColumn(next)
    } else {
      decorateContentColumn(next)
    }
    return next
  })
}

function storageKey(userId, tableKey) {
  return `${PREFIX}${userId || "anonymous"}-${tableKey || "default"}`
}

function normalizeWidths(widths) {
  if (!widths || typeof widths !== "object") return {}
  const next = {}
  Object.keys(widths).forEach(id => {
    const n = Number(widths[id])
    if (!Number.isNaN(n) && n > 0) {
      next[String(id)] = Math.round(n)
    }
  })
  return next
}

export function loadColumnSetting(userId, tableKey) {
  if (!tableKey) return null
  const raw = storage.get(storageKey(userId, tableKey))
  if (!raw || typeof raw !== "object") return null
  return {
    order: Array.isArray(raw.order) ? raw.order.map(String) : [],
    hidden: Array.isArray(raw.hidden) ? raw.hidden.map(String) : [],
    widths: normalizeWidths(raw.widths)
  }
}

export function saveColumnSetting(userId, tableKey, setting) {
  if (!tableKey) return
  storage.set(storageKey(userId, tableKey), {
    order: (setting.order || []).map(String),
    hidden: (setting.hidden || []).map(String),
    widths: normalizeWidths(setting.widths)
  })
}

export function removeColumnSetting(userId, tableKey) {
  if (!tableKey) return
  storage.remove(storageKey(userId, tableKey))
}

/**
 * 将已记忆的列宽合并到列定义
 * 操作列若曾被收窄过窄，抬回最小宽度，避免按钮显示不全
 */
export function applyColumnWidths(columns, widths) {
  const map = normalizeWidths(widths)
  if (!Object.keys(map).length) return columns || []
  return (columns || []).map(col => {
    const id = col && col._colId
    if (id && map[id] != null) {
      let w = map[id]
      if (
        isOperationColumn(col) &&
        (col.width == null || col.width >= 100) &&
        w < OP_COL_MIN_WIDTH
      ) {
        w = OP_COL_MIN_WIDTH
      }
      return { ...col, width: w }
    }
    return col
  })
}

/**
 * 根据偏好生成可见列（含顺序与列宽）
 * - 新列（代码新增、不在 order 中）默认显示，插在锁定列之前
 * - 锁定列始终可见，相对顺序保持在 original 末尾固定区
 */
export function applyColumnSetting(originalColumns, setting) {
  const originals = cloneColumns(originalColumns)
  if (!setting) {
    return originals
  }

  const byId = {}
  originals.forEach(col => {
    byId[col._colId] = col
  })

  const locked = originals.filter(isLockedColumn)
  const unlocked = originals.filter(col => !isLockedColumn(col))
  const lockedIds = new Set(locked.map(c => c._colId))
  const hiddenSet = new Set(
    (setting.hidden || []).filter(id => !lockedIds.has(id))
  )

  const order = (setting.order || []).filter(id => byId[id] && !lockedIds.has(id))
  const orderedUnlocked = []
  const used = new Set()

  order.forEach(id => {
    if (byId[id] && !used.has(id)) {
      orderedUnlocked.push(byId[id])
      used.add(id)
    }
  })

  // 代码新增列：不在 order 中的非锁定列，按原顺序追加到可调区末尾
  unlocked.forEach(col => {
    if (!used.has(col._colId)) {
      orderedUnlocked.push(col)
      used.add(col._colId)
    }
  })

  const visibleUnlocked = orderedUnlocked.filter(
    col => !hiddenSet.has(col._colId)
  )

  const visible = [...visibleUnlocked, ...locked]
  return applyColumnWidths(visible, setting.widths)
}

/**
 * 从当前完整列元数据 + 可见偏好构建 setting
 * @param {Array} allColumns 含锁定列的全部列（通常为 original）
 * @param {string[]} visibleOrder 当前可调列的显示顺序（仅非锁定）
 * @param {string[]} hiddenIds 隐藏的列 id
 */
export function buildSetting(allColumns, visibleOrder, hiddenIds, widths) {
  const unlocked = (allColumns || []).filter(col => !isLockedColumn(col))
  const unlockedIds = unlocked.map(c => c._colId)
  const order = []
  const used = new Set()

  ;(visibleOrder || []).forEach(id => {
    if (unlockedIds.includes(id) && !used.has(id)) {
      order.push(id)
      used.add(id)
    }
  })
  unlockedIds.forEach(id => {
    if (!used.has(id)) {
      order.push(id)
      used.add(id)
    }
  })

  const lockedIds = new Set(
    (allColumns || []).filter(isLockedColumn).map(c => c._colId)
  )
  const hidden = (hiddenIds || []).filter(
    id => unlockedIds.includes(id) && !lockedIds.has(id)
  )

  return {
    order,
    hidden,
    widths: normalizeWidths(widths)
  }
}

/**
 * 可见列 width 求和，供 scroll.x 使用
 * 必须约等于各列像素宽之和（+ 勾选列），过大时右固定操作列会与内容列拉开空隙
 * @param {Array} columns
 * @param {number|object} fallbackOrOptions 兼容旧签名：数字 fallback，或 { fallback, selection }
 */
export function calcScrollX(columns, fallbackOrOptions = 1200) {
  const options =
    fallbackOrOptions && typeof fallbackOrOptions === "object"
      ? fallbackOrOptions
      : { fallback: fallbackOrOptions }
  const fallback = options.fallback != null ? options.fallback : 1200
  const selectionWidth =
    options.selection === false
      ? 0
      : options.selectionWidth != null
      ? options.selectionWidth
      : 60

  let sum = 0
  let hasWidth = false
  ;(columns || []).forEach(col => {
    if (!col) return
    if (typeof col.width === "number" && col.width > 0) {
      sum += col.width
      hasWidth = true
    } else if (typeof col.width === "string") {
      const n = parseInt(col.width, 10)
      if (!Number.isNaN(n) && n > 0) {
        sum += n
        hasWidth = true
      }
    } else {
      // 无宽度时占位，避免被当成弹性列吞掉空间
      sum += 120
      hasWidth = true
    }
  })
  if (!hasWidth) return fallback
  // 仅按列宽合计，不再抬到 600+，否则窄表 + fixed 会出现操作列横缝
  return sum + selectionWidth
}

/**
 * 将当前可见列宽快照为数字 width（用于拖拽前锁定，防止重分配）
 */
export function lockColumnPixelWidths(columns, widthMap) {
  const map = widthMap || {}
  ;(columns || []).forEach(col => {
    if (!col || !col._colId) return
    if (map[col._colId] != null) {
      col.width = Math.max(48, Math.round(Number(map[col._colId])))
      return
    }
    if (typeof col.width === "number" && col.width > 0) return
    if (typeof col.width === "string") {
      const n = parseInt(col.width, 10)
      if (!Number.isNaN(n) && n > 0) {
        col.width = n
        return
      }
    }
    col.width = 120
  })
  return columns
}

/**
 * 供设置面板使用的列项列表（全部非锁定 + 锁定展示）
 */
export function getSettingItems(originalColumns, setting) {
  const originals = cloneColumns(originalColumns)
  const hiddenSet = new Set((setting && setting.hidden) || [])

  // 面板顺序：当前可见非锁定顺序 + 已隐藏非锁定（按 original） + 锁定列（禁用）
  const locked = originals.filter(isLockedColumn)
  const unlocked = originals.filter(col => !isLockedColumn(col))

  const order =
    setting && setting.order && setting.order.length
      ? setting.order
      : unlocked.map(c => c._colId)

  const items = []
  const used = new Set()

  order.forEach(id => {
    const col = unlocked.find(c => c._colId === id)
    if (col && !used.has(id)) {
      items.push({
        id: col._colId,
        title: col.title || col._colId,
        locked: false,
        visible: !hiddenSet.has(col._colId)
      })
      used.add(id)
    }
  })

  unlocked.forEach(col => {
    if (!used.has(col._colId)) {
      items.push({
        id: col._colId,
        title: col.title || col._colId,
        locked: false,
        visible: !hiddenSet.has(col._colId)
      })
      used.add(col._colId)
    }
  })

  locked.forEach(col => {
    items.push({
      id: col._colId,
      title: col.title || col._colId,
      locked: true,
      visible: true
    })
  })

  return items
}
